import { useEffect, useState } from 'react';
import { apiGet, apiPost, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { useModal } from '../contexts/ModalContext';
import { useVerificationViews } from '../lib/useVerificationViews';
import { externalValidationDefaults, type ExternalValidationForm, type StateLicenseRow } from '../lib/externalValidationDefaults';
import { OfficialCheckCell } from '../components/OfficialCheckCell';
import { EvidencePanel } from '../components/EvidencePanel';
import { VerificationAttentionBanner } from '../components/VerificationAttentionBanner';
import {
  phaseLabel,
  phaseOf,
  summarizeAttention,
  type ApiValidation,
} from '../lib/verificationView';

interface PendingSpecialist {
  id: string;
  name: string;
  email: string;
  specialty?: string;
  verificationStatus?: string;
  licenseNumber?: string;
  npiNumber?: string;
  verificationData?: { stateLicenses?: StateLicenseRow[] } | null;
  licensedJurisdictions?: Array<{ country?: string; region?: string }> | null;
  malpracticeInsuranceStatus?: 'not_submitted' | 'pending' | 'verified' | 'rejected' | 'expired';
  /** Compact official-check block, present when the backend's v2 pipeline flag is on. */
  validation?: ApiValidation | null;
}

const verificationTone = (status?: string) =>
  status === 'IDENTITY_VERIFIED' ? 'good' : status === 'IN_REVIEW' ? 'warn' : 'neutral';

const malpracticeTone = (status?: string) =>
  status === 'verified' ? 'good' : status === 'pending' ? 'warn' : status === 'rejected' ? 'bad' : 'neutral';

export function SpecialistVerificationPage() {
  const { promptText, alert } = useModal();
  const { data, loading, error, reload } = useApiResource(
    () => apiGet<PendingSpecialist[]>('/verification/admin/pending'),
    [],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [externalValidationId, setExternalValidationId] = useState<string | null>(null);
  const [externalValidationForm, setExternalValidationForm] = useState<ExternalValidationForm | null>(null);
  const { views, live, error: liveError } = useVerificationViews();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [jobErrors, setJobErrors] = useState<Record<string, string | null>>({});

  const toggleDetails = (id: string) => setExpandedId((cur) => (cur === id ? null : id));

  // The latest job's error is not part of the live view, so it is fetched while a drawer is open
  // and again whenever that specialist's job changes (a re-run must not show the old error).
  const openView = expandedId ? views.get(expandedId) : undefined;
  useEffect(() => {
    if (!expandedId) return;
    let cancelled = false;
    apiGet<{ job: { status: string; lastError: string | null } | null }>(
      `/verification/admin/specialists/${expandedId}/validation`,
    )
      .then((r) => {
        if (!cancelled)
          setJobErrors((prev) => ({ ...prev, [expandedId]: r.job?.status === 'failed' ? r.job.lastError : null }));
      })
      .catch(() => {
        if (!cancelled) setJobErrors((prev) => ({ ...prev, [expandedId]: null }));
      });
    return () => {
      cancelled = true;
    };
  }, [expandedId, openView?.jobId, openView?.generation, openView?.jobStatus]);

  const pendingIds = new Set((data ?? []).map((s) => s.id));
  const pendingAttention = summarizeAttention([...views.values()].filter((v) => pendingIds.has(v.specialistId)));
  // Review flags are about specialists the pending list does not show (approved ones), so they
  // are counted and named across every view; failed / manual review only for the rows here.
  const allAttention = summarizeAttention(views.values());
  const attention = {
    ...pendingAttention,
    needsReview: allAttention.needsReview,
    needsReviewNames: allAttention.needsReviewNames,
  };
  // While the listener is down the last-known views may be stale: the API block is fresher.
  const listenerDown = !live && liveError !== null;

  const runAction = async (id: string, action: () => Promise<unknown>) => {
    setBusyId(id);
    setActionError(null);
    try {
      await action();
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const approve = async (id: string) => {
    const reason = await promptText({
      title: 'Approve specialist',
      message: 'Approval note (shown to the specialist):',
      defaultValue: 'All documents verified successfully',
    });
    if (reason === null) return;
    void runAction(id, () => apiPost(`/verification/admin/${id}/approve`, { reason }));
  };

  const reject = async (id: string) => {
    const reason = await promptText({
      title: 'Reject specialist',
      message: 'Rejection reason (shown to the specialist):',
      required: true,
    });
    if (!reason) return;
    void runAction(id, () => apiPost(`/verification/admin/${id}/reject`, { reason }));
  };

  const reverify = async (id: string) => {
    const reason = await promptText({
      title: 'Trigger re-verification',
      message: 'Reason for triggering re-verification:',
      required: true,
    });
    if (!reason) return;
    void runAction(id, () => apiPost(`/verification/admin/specialist/${id}/reverify`, { reason }));
  };

  const reviewMalpractice = async (id: string, approved: boolean) => {
    const rejectionReason = approved
      ? undefined
      : (await promptText({
          title: 'Reject malpractice insurance',
          message: 'Reason for rejecting the malpractice insurance policy:',
          required: true,
        })) || undefined;
    if (!approved && !rejectionReason) return;
    void runAction(id, () =>
      apiPost(`/verification/admin/${id}/malpractice-insurance/review`, { approved, rejectionReason }),
    );
  };

  const openExternalValidation = (s: PendingSpecialist) => {
    setExternalValidationId(s.id);
    setExternalValidationForm(externalValidationDefaults(s));
  };

  const submitExternalValidation = async (id: string) => {
    if (!externalValidationForm) return;
    const { country, firstName, lastName, licenseNumber, state, colombiaDocumentType, colombiaDocumentNumber, notes } =
      externalValidationForm;
    if (!firstName.trim() || !lastName.trim()) return;
    setBusyId(id);
    setActionError(null);
    try {
      const result = await apiPost(`/verification/admin/${id}/run-external-validation`, {
        country,
        firstName,
        lastName,
        licenseNumber: licenseNumber || undefined,
        state: state || undefined,
        colombiaDocumentType: colombiaDocumentType || undefined,
        colombiaDocumentNumber: colombiaDocumentNumber || undefined,
        notes: notes || undefined,
      });
      setExternalValidationId(null);
      setExternalValidationForm(null);
      const accepted = (result as { accepted?: boolean } | null)?.accepted === true;
      await alert(
        accepted
          ? 'Official verification queued. The result appears in the Official check column as soon as it is ready.'
          : 'Official verification started.',
      );
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'External validation failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Specialist Verification</h1>
          <p>Specialists in IN_REVIEW or IDENTITY_VERIFIED, plus pending malpractice insurance review.</p>
        </div>
        <button className="btn" onClick={reload}>
          Refresh
        </button>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}
      <VerificationAttentionBanner summary={attention} live={live} error={liveError} />

      <DataTable
        id="specialist-verification"
        rows={data}
        loading={loading}
        empty="No specialists pending review."
        noun="specialists"
        getRowKey={(s) => s.id}
        expandedRow={(s) => {
          const view = listenerDown ? undefined : views.get(s.id);
          return view && expandedId === s.id ? (
            <EvidencePanel view={view} jobError={jobErrors[s.id]} />
          ) : null;
        }}
        columns={[
          { key: 'name', header: 'Name', value: (s) => s.name || '\u2014' },
          { key: 'email', header: 'Email', value: (s) => s.email },
          { key: 'specialty', header: 'Specialty', value: (s) => s.specialty || '\u2014', filter: 'select' },
          {
            key: 'verification',
            header: 'Verification',
            value: (s) => s.verificationStatus || 'UNKNOWN',
            filter: 'select',
            render: (s) => (
              <StatusBadge label={s.verificationStatus || 'UNKNOWN'} tone={verificationTone(s.verificationStatus)} />
            ),
          },
          {
            key: 'license',
            header: 'License / NPI',
            value: (s) => `${s.licenseNumber || '\u2014'}${s.npiNumber ? ` / ${s.npiNumber}` : ''}`,
          },
          {
            key: 'malpractice',
            header: 'Malpractice insurance',
            value: (s) => s.malpracticeInsuranceStatus || 'not_submitted',
            filter: 'select',
            render: (s) => (
              <StatusBadge
                label={s.malpracticeInsuranceStatus || 'not_submitted'}
                tone={malpracticeTone(s.malpracticeInsuranceStatus)}
              />
            ),
          },
          {
            key: 'official',
            header: 'Official check',
            value: (s) => phaseLabel(phaseOf(listenerDown ? undefined : views.get(s.id), s.validation)),
            filter: 'select',
            render: (s) => (
              <OfficialCheckCell
                view={listenerDown ? undefined : views.get(s.id)}
                apiValidation={s.validation}
                name={s.name}
                open={expandedId === s.id}
                onToggle={() => toggleDetails(s.id)}
              />
            ),
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (s) => (
              <>
              <div className="row-actions">
                <button className="btn btn-primary" disabled={busyId === s.id} onClick={() => approve(s.id)}>
                  Approve
                </button>
                <button className="btn btn-danger" disabled={busyId === s.id} onClick={() => reject(s.id)}>
                  Reject
                </button>
                <button className="btn" disabled={busyId === s.id} onClick={() => reverify(s.id)}>
                  Reverify
                </button>
                <button className="btn" disabled={busyId === s.id} onClick={() => openExternalValidation(s)}>
                  {views.has(s.id) ? 'Re-run official verification' : 'Run official verification'}
                </button>
                {s.malpracticeInsuranceStatus === 'pending' && (
                  <>
                    <button
                      className="btn btn-primary"
                      disabled={busyId === s.id}
                      onClick={() => reviewMalpractice(s.id, true)}
                    >
                      Approve insurance
                    </button>
                    <button
                      className="btn btn-danger"
                      disabled={busyId === s.id}
                      onClick={() => reviewMalpractice(s.id, false)}
                    >
                      Reject insurance
                    </button>
                  </>
                )}
              </div>

              {externalValidationId === s.id && externalValidationForm && (
                <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px', marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '24rem' }}>
                  <label>
                    Country
                    <select
                      value={externalValidationForm.country}
                      onChange={(e) =>
                        setExternalValidationForm({ ...externalValidationForm, country: e.target.value as 'US' | 'CO' })
                      }
                    >
                      <option value="US">United States</option>
                      <option value="CO">Colombia</option>
                    </select>
                  </label>
                  <label>
                    First name
                    <input
                      type="text"
                      value={externalValidationForm.firstName}
                      onChange={(e) => setExternalValidationForm({ ...externalValidationForm, firstName: e.target.value })}
                    />
                  </label>
                  <label>
                    Last name
                    <input
                      type="text"
                      value={externalValidationForm.lastName}
                      onChange={(e) => setExternalValidationForm({ ...externalValidationForm, lastName: e.target.value })}
                    />
                  </label>
                  {externalValidationForm.country === 'US' ? (
                    <>
                      <label>
                        License number
                        <input
                          type="text"
                          value={externalValidationForm.licenseNumber}
                          onChange={(e) =>
                            setExternalValidationForm({ ...externalValidationForm, licenseNumber: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        State
                        <input
                          type="text"
                          value={externalValidationForm.state}
                          onChange={(e) => setExternalValidationForm({ ...externalValidationForm, state: e.target.value })}
                        />
                      </label>
                    </>
                  ) : (
                    <>
                      <label>
                        Document type (CC/CE/PPT/TI)
                        <input
                          type="text"
                          value={externalValidationForm.colombiaDocumentType}
                          onChange={(e) =>
                            setExternalValidationForm({ ...externalValidationForm, colombiaDocumentType: e.target.value })
                          }
                        />
                      </label>
                      <label>
                        Document number
                        <input
                          type="text"
                          value={externalValidationForm.colombiaDocumentNumber}
                          onChange={(e) =>
                            setExternalValidationForm({ ...externalValidationForm, colombiaDocumentNumber: e.target.value })
                          }
                        />
                      </label>
                    </>
                  )}
                  <label>
                    Notes
                    <input
                      type="text"
                      value={externalValidationForm.notes}
                      onChange={(e) => setExternalValidationForm({ ...externalValidationForm, notes: e.target.value })}
                    />
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-primary"
                      disabled={busyId === s.id || !externalValidationForm.firstName.trim() || !externalValidationForm.lastName.trim()}
                      onClick={() => void submitExternalValidation(s.id)}
                    >
                      {busyId === s.id ? 'Running…' : 'Submit'}
                    </button>
                    <button
                      className="btn"
                      onClick={() => {
                        setExternalValidationId(null);
                        setExternalValidationForm(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              </>
            ),
          },
        ]}
      />
    </div>
  );
}
